<template>
  <BgImageLayout>
    <ConfirmationDialog
      ref="confirmDialog"
      cancel-label="leave without saving"
      confirm-label="update"
      confirm-btn-type="tertiary"
      title="Update before navigating away ?"
    />

    <Grid gtr="auto 1fr" class="full-height shadow3 list-view-subscriptions">
      <CardHeader
        :ag-grid="agGrid"
        @create-new-subscription="() => $refs.viewMode.openCreateModal()"
        @export="() => $refs[mode].exportToExcel()"
        @toggle-edit-mode="toggleEditMode"
        @clear-filters="clearFilters"
      />

      <Grid gtr="auto 1fr" gap="var(--r-space-sm)" class="body bg-white-1 q-pl-md q-pr-lg q-py-lg">
        <template v-if="tableState.chipFilter">
          <div class="table-header q-pr-lg">
            <ChipFilters class="chip-filters" />

            <BulkUpdate
              v-if="editMode && editModeLoaded"
              ref="bulkUpdate"
              :ag-grid="agGrid"
              class="bulk-update"
              @bulk-update="onBulkUpdate"
            />
          </div>

          <EditMode
            v-if="editMode"
            :key="rerenderEditMode"
            ref="editMode"
            @ag-grid="agGrid = $event"
            @hook:created="editModeLoaded = true"
          />

          <ViewMode
            v-else
            :key="rerenderViewMode"
            ref="viewMode"
            @ag-grid="agGrid = $event"
            @hook:created="editModeLoaded = false"
          />
        </template>

        <QInnerLoading :showing="!tableState.chipFilter">
          <QSpinnerHourglass size="3.5rem" color="primary-1" />
        </QInnerLoading>
      </Grid>
    </Grid>
  </BgImageLayout>
</template>

<script>
import ListViewSubscriptionsModule from './list-view-subscriptions'
// Libraries
import { mapState } from 'vuex'
// Components
import CardHeader from './components/CardHeader'
import ChipFilters from './components/ChipFilters'
import BulkUpdate from './components/BulkUpdate'
import EditMode from './components/edit-mode/EditMode'
import ViewMode from './components/view-mode/ViewMode'
import ConfirmationDialog from '@/ui/ConfirmationDialog'

export default {
  components: {
    CardHeader,
    ChipFilters,
    BulkUpdate,
    EditMode,
    ViewMode,
    ConfirmationDialog
  },

  data() {
    return {
      agGrid: null,
      editModeLoaded: false,
      rerenderEditMode: 1,
      rerenderViewMode: 1
    }
  },

  computed: {
    ...mapState('lv-subscriptions', ['exportLoading', 'tableState']),

    editMode: {
      get() {
        return this.$store.state['lv-subscriptions'].editMode
      },

      set(newVal) {
        this.$store.commit('lv-subscriptions/setEditMode', newVal)
      }
    },

    mode() {
      return this.editMode ? 'editMode' : 'viewMode'
    }
  },

  beforeCreate() {
    this.$store.registerModule('lv-subscriptions', ListViewSubscriptionsModule)
  },

  beforeRouteLeave(to, from, next) {
    if (to.name === 'login') next()
    else this.checkForUnsavedChanges(next)
  },

  beforeRouteUpdate(to, from, next) {
    if (to.name === 'login') next()
    else this.checkForUnsavedChanges(next)
  },

  created() {
    this.$store.dispatch('subscriptions/subscriptionPeriods/getCreateFormData')
    this.$store.dispatch('lv-subscriptions/getAndSetDepsMetadataAndTableState')
    // Reset vendor and product id in state
    this.$store.dispatch('subscriptions/subscriptionPeriods/setProductId', null)
    this.$store.dispatch('subscriptions/subscriptionPeriods/setVendorId', null)
    this.$store.dispatch('subscriptions/subscriptionPeriods/setNewSubscriptionPeriod', null)

    const preventOrNot = e => {
      if (!this.$refs?.bulkUpdate.thereIsUnsavedChanges()) return
      e.preventDefault()
      e.returnValue = ''
      return true
    }

    window.addEventListener('beforeunload', preventOrNot)
    this.$once('hook:beforeDestroy', () => {
      window.removeEventListener('beforeunload', preventOrNot)
    })
  },

  beforeDestroy() {
    if (!this.editMode) this.storeTableState()
    this.$store.dispatch('lv-subscriptions/saveTableState')
  },

  destroyed() {
    this.$store.unregisterModule('lv-subscriptions')
  },

  methods: {
    toggleEditMode() {
      if (!this.editMode) {
        this.storeTableState()
        this.editMode = true
      } else if (this.$refs.bulkUpdate.thereIsUnsavedChanges()) {
        const onCancel = hide => {
          hide()
          this.editMode = false
        }

        const onConfirm = (hide, setLoading) => {
          setLoading()
          this.$refs.bulkUpdate
            .update(false)
            .then(() => {
              hide()
              this.editMode = false
            })
            .catch(() => {
              setLoading(false)
            })
        }

        this.showConfirmDialog(onCancel, onConfirm)
      } else {
        this.editMode = false
      }
    },

    checkForUnsavedChanges(allowNavigation) {
      if (!this.editMode) allowNavigation()
      else if (!this.$refs.bulkUpdate.thereIsUnsavedChanges()) allowNavigation()
      else {
        const onCancel = hide => {
          allowNavigation()
          hide()
        }

        const onConfirm = (hide, setLoading) => {
          setLoading()
          this.$refs.bulkUpdate
            .update(false)
            .then(() => {
              allowNavigation()
              hide()
            })
            .catch(() => {
              setLoading(false)
            })
        }

        this.showConfirmDialog(onCancel, onConfirm)
      }
    },

    showConfirmDialog(onCancel, onConfirm) {
      const { rowsText, col } = this.$refs.bulkUpdate.getInfo()

      const dialog = this.$refs.confirmDialog.show({
        body: `You have the following unsaved changes:\nUpdate (${col})  for ${rowsText}
          `
      })

      dialog.onCancel(onCancel)
      dialog.onConfirm(onConfirm)
    },

    storeTableState() {
      const filterModel = this.agGrid.api.getFilterModel()
      const columnState = this.agGrid.columnApi.getColumnState()

      this.$store.commit('lv-subscriptions/setTableStateFilter', filterModel)
      this.$store.commit('lv-subscriptions/setTableStateColumns', columnState)
    },

    onBulkUpdate() {
      this.$store.commit('lv-subscriptions-edit-mode/setSelectAll', false)
      this.$store.commit('lv-subscriptions-edit-mode/setRowsSelected', [])
      this.$refs.editMode.refreshTable()
    },

    clearFilters() {
      this.$store.commit('lv-subscriptions/setTableStateFilter', null)
      if (this.mode === 'editMode') this.rerenderEditMode++
      else this.rerenderViewMode++
    }
  }
}
</script>

<style lang="scss" scoped>
.list-view-subscriptions {
  border-radius: 2px;
  overflow: hidden;

  .table-header {
    --gap: var(--r-space-lg);

    /* flexbox with gap. Credit: https://coryrylan.com/blog/css-gap-space-with-flexbox */
    display: flex;
    flex-wrap: wrap;
    margin: calc(-1 * var(--gap)) 0 0 calc(-1 * var(--gap));
    width: calc(100% + var(--gap));

    > * {
      margin: var(--gap) 0 0 var(--gap);
    }

    > .chip-filters,
    > .bulk-update {
      flex: 1;
    }
  }
}
</style>
